import { useEffect, useRef, useState } from 'react'
import { ArrowUp, BookOpen, Boxes, Calculator, Check, ChevronDown, ChevronLeft, Cpu, FileText, Globe, ImageIcon, Lightbulb, Plus, Square, Wand2, Workflow, X, Zap } from 'lucide-react'
import { DEFAULT_MAX_FILES, type Attachment, type ChatMode } from '@shared/types'
import { MODELS, MODEL_IDS } from '@shared/models'
import { useStore } from '../../store'
import { cn } from '../../lib/utils'

const MODES: { id: ChatMode; label: string; sub: string; icon: typeof Zap }[] = [
  { id: 'fast', label: 'Fast', sub: 'Quick answers · low reasoning', icon: Zap },
  { id: 'auto', label: 'Auto', sub: 'Chooses Fast or Expert', icon: Wand2 },
  { id: 'expert', label: 'Expert', sub: 'Deeper reasoning + research', icon: Lightbulb },
  { id: 'heavy', label: 'Heavy', sub: 'Team of Experts (multi-agent)', icon: Boxes },
]

const TEXT_EXT = /\.(txt|md|markdown|csv|tsv|json|log|js|ts|jsx|tsx|py|html|xml|ya?ml|sql|sh|css)$/i
const MAX_FILE = 50 * 1024 * 1024 // 50 MB — any file type; only this size cap remains

const readDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result as string)
    r.onerror = reject
    r.readAsDataURL(file)
  })

async function readFile(file: File): Promise<Attachment | null> {
  if (file.size > MAX_FILE) return null
  if (file.type.startsWith('image/')) {
    return { name: file.name, mime: file.type, kind: 'image', dataUrl: await readDataUrl(file) }
  }
  if (TEXT_EXT.test(file.name) || file.type.startsWith('text/') || file.type === 'application/json') {
    return { name: file.name, mime: file.type || 'text/plain', kind: 'text', text: await file.text() }
  }
  // Anything else (PDF, Word, Excel, PowerPoint, zip, …) → binary upload. The bytes
  // ride along as a base64 data URL; the server writes them into the chat's sandbox
  // uploads dir so the document skills can read them.
  return { name: file.name, mime: file.type || 'application/octet-stream', kind: 'binary', dataUrl: await readDataUrl(file) }
}

export function Composer() {
  const [text, setText] = useState('')
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const ref = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const streaming = useStore((s) => s.streaming)
  const maxFiles = useStore((s) => s.user?.maxFiles) ?? DEFAULT_MAX_FILES
  const send = useStore((s) => s.send)
  const stop = useStore((s) => s.stop)
  const setOrb = useStore((s) => s.setOrb)
  const liveSearch = useStore((s) => s.liveSearch)
  const toggleLiveSearch = useStore((s) => s.toggleLiveSearch)
  const toggleCalculator = useStore((s) => s.toggleCalculator)
  const loadIntegrations = useStore((s) => s.loadIntegrations)
  const [menuOpen, setMenuOpen] = useState(false)
  const [submenu, setSubmenu] = useState<null | 'skills' | 'mcp'>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const prefill = useStore((s) => s.composerPrefill)
  const isAdmin = useStore((s) => s.user?.role === 'admin')
  const chatTrading = useStore((s) => s.chatTrading)
  const setChatTrading = useStore((s) => s.setChatTrading)

  useEffect(() => {
    if (!prefill) return
    setText(prefill.text)
    requestAnimationFrame(() => {
      const el = ref.current
      if (!el) return
      el.focus()
      el.setSelectionRange(prefill.text.length, prefill.text.length)
    })
  }, [prefill])

  useEffect(() => {
    if (!menuOpen) {
      setSubmenu(null)
      return
    }
    void loadIntegrations() // refresh the skills/connectors lists when opening
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen, loadIntegrations])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }, [text])

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return
    const read = await Promise.all(Array.from(files).map(readFile))
    const ok = read.filter((a): a is Attachment => a !== null)
    const skipped = read.length - ok.length
    if (skipped > 0) alert(`${skipped} file(s) skipped — each file must be ≤50MB.`)
    setAttachments((prev) => [...prev, ...ok].slice(0, maxFiles))
  }

  const submit = () => {
    const t = text.trim()
    if ((!t && attachments.length === 0) || streaming) return
    setText('')
    const atts = attachments
    setAttachments([])
    void send(t, atts)
  }

  return (
    <div className="px-4 pb-4 pt-2">
      <div className="mx-auto max-w-3xl">
        <div className="composer-shell glass p-2 shadow-[var(--e-2)]" style={{ borderRadius: 'var(--radius-lg)' }}>
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-1 pb-1.5">
              {attachments.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] bg-[var(--color-surface-2)] px-2 py-1 text-[11px] text-fg-muted">
                  {a.kind === 'image' ? <ImageIcon size={12} /> : <FileText size={12} />}
                  <span className="max-w-[140px] truncate">{a.name}</span>
                  <button onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))} className="text-fg-subtle hover:text-fg" title="Remove">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <textarea
            ref={ref}
            value={text}
            rows={1}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                submit()
              }
            }}
            onFocus={() => !streaming && setOrb('listening')}
            onBlur={() => !streaming && setOrb('idle')}
            placeholder="Ask about the market — a ticker, a thesis, a chart, a file…"
            className="block w-full resize-none bg-transparent px-2.5 py-2 text-[14.5px] leading-relaxed text-fg placeholder:text-fg-faint focus:outline-none"
          />
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-1.5">
              {/* Claude-style "+" menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen((o) => !o)}
                  title="Add"
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                    menuOpen ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-subtle hover:text-fg-muted hover:bg-[var(--color-surface-2)]',
                  )}
                >
                  <Plus size={17} className={cn('transition-transform', menuOpen && 'rotate-45')} />
                </button>
                {menuOpen && (
                  <div className="absolute bottom-full left-0 z-30 mb-2 w-64 overflow-hidden rounded-xl border border-[var(--line-soft)] bg-[var(--color-surface-2)] p-1 shadow-[var(--e-3)]">
                    {submenu === null ? (
                      <>
                        <MenuItem icon={<ImageIcon size={15} />} label="Add files or photos" hint="Ctrl+U" onClick={() => { setMenuOpen(false); fileRef.current?.click() }} />
                        <MenuItem icon={<Calculator size={15} />} label="Calculator" onClick={() => { setMenuOpen(false); toggleCalculator(true) }} />
                        <MenuItem icon={<Globe size={15} />} label="Web search" checked={liveSearch} onClick={() => toggleLiveSearch()} />
                        <div className="my-1 h-px bg-[var(--line-soft)]" />
                        <MenuItem icon={<BookOpen size={15} />} label="Skills" chevron onClick={() => setSubmenu('skills')} />
                        <MenuItem icon={<Boxes size={15} />} label="Connectors (MCP)" chevron onClick={() => setSubmenu('mcp')} />
                        {isAdmin && (
                          <>
                            <div className="my-1 h-px bg-[var(--line-soft)]" />
                            <MenuItem
                              icon={<Workflow size={15} />}
                              label="Trading (workers & orders)"
                              checked={chatTrading}
                              onClick={() => setChatTrading(!chatTrading)}
                            />
                          </>
                        )}
                      </>
                    ) : (
                      <ToolPanel kind={submenu} onBack={() => setSubmenu(null)} onClose={() => setMenuOpen(false)} />
                    )}
                  </div>
                )}
              </div>
              {isAdmin && chatTrading && (
                <button
                  onClick={() => setChatTrading(false)}
                  title="Trading is on for this chat — Libera can plan and explain trade workers here. Click to turn off."
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--accent-wash)] px-2.5 py-1 text-[12px] text-fg"
                >
                  <Workflow size={13} /> Trading
                </button>
              )}
              {liveSearch && (
                <button
                  onClick={() => toggleLiveSearch()}
                  title="Web search is on — click to turn off"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--accent-wash)] px-2.5 py-1 text-[12px] text-fg"
                >
                  <Globe size={13} /> Live
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  void onFiles(e.target.files)
                  e.target.value = ''
                }}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <ModelMenu />
              <ModeMenu />
              {streaming ? (
                <button onClick={stop} title="Stop" className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-surface-3)] text-fg transition-opacity hover:opacity-90">
                  <Square size={12} className="fill-current" />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={!text.trim() && attachments.length === 0}
                  title="Send  ·  Enter"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-accent)] text-[var(--color-base)] transition-opacity disabled:opacity-30"
                >
                  <ArrowUp size={16} strokeWidth={2.4} />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="mt-1.5 text-center text-[10.5px] text-fg-faint">
          Libera can be wrong — verify before trading. Not financial advice.
        </div>
      </div>
    </div>
  )
}

// Model picker (the two-model registry: Grok 4.3 / Grok 4.6). Per-chat, changeable
// mid-chat; persists via the store like the mode.
function ModelMenu() {
  const chatModel = useStore((s) => s.chatModel)
  const setChatModel = useStore((s) => s.setChatModel)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const cur = MODELS[chatModel] ?? MODELS[MODEL_IDS[0]]
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Model"
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12.5px] transition-colors',
          open ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-muted hover:bg-[var(--color-surface-2)] hover:text-fg',
        )}
      >
        <Cpu size={13} />
        {cur.label}
        <ChevronDown size={13} className={cn('transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute bottom-full right-0 z-30 mb-2 w-72 overflow-hidden rounded-xl border border-[var(--line-soft)] bg-[var(--color-surface-2)] p-1 shadow-[var(--e-3)]">
          {MODEL_IDS.map((id) => {
            const m = MODELS[id]
            const active = chatModel === id
            return (
              <button
                key={id}
                onClick={() => {
                  setChatModel(id)
                  setOpen(false)
                }}
                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-[var(--color-surface-3)]"
              >
                <Cpu size={16} className="shrink-0 text-fg-muted" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] text-fg">{m.label}</span>
                  <span className="block truncate text-[11px] text-fg-subtle">{m.blurb}</span>
                </span>
                {active && <Check size={15} className="shrink-0 text-fg" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Grok-style model-mode picker: a pill that shows the current mode and opens a dropdown
// (Fast / Auto / Expert / Heavy). Per-chat, changeable mid-chat; persists via the store.
function ModeMenu() {
  const chatMode = useStore((s) => s.chatMode)
  const setChatMode = useStore((s) => s.setChatMode)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const cur = MODES.find((m) => m.id === chatMode) ?? MODES[0]
  const CurIcon = cur.icon
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Model mode"
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12.5px] transition-colors',
          open ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-muted hover:bg-[var(--color-surface-2)] hover:text-fg',
        )}
      >
        <CurIcon size={13} />
        {cur.label}
        <ChevronDown size={13} className={cn('transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute bottom-full right-0 z-30 mb-2 w-64 overflow-hidden rounded-xl border border-[var(--line-soft)] bg-[var(--color-surface-2)] p-1 shadow-[var(--e-3)]">
          {MODES.map((m) => {
            const Icon = m.icon
            const active = chatMode === m.id
            return (
              <button
                key={m.id}
                onClick={() => {
                  setChatMode(m.id)
                  setOpen(false)
                }}
                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-[var(--color-surface-3)]"
              >
                <Icon size={16} className="shrink-0 text-fg-muted" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] text-fg">{m.label}</span>
                  <span className="block truncate text-[11px] text-fg-subtle">{m.sub}</span>
                </span>
                {active && <Check size={15} className="shrink-0 text-fg" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function MenuItem({
  icon, label, hint, checked, chevron, onClick,
}: {
  icon: React.ReactNode
  label: string
  hint?: string
  checked?: boolean
  chevron?: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-fg-muted transition-colors hover:bg-[var(--color-surface-3)] hover:text-fg"
    >
      <span className="text-fg-subtle">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {checked && <Check size={14} className="text-[var(--color-accent-bright)]" />}
      {hint && <span className="text-[11px] text-fg-faint">{hint}</span>}
      {chevron && <span className="text-fg-faint">›</span>}
    </button>
  )
}

/** Per-chat picker for Skills / MCP connectors — toggles apply to THIS chat only. */
function ToolPanel({ kind, onBack, onClose }: { kind: 'skills' | 'mcp'; onBack: () => void; onClose: () => void }) {
  const skillsList = useStore((s) => s.skillsList)
  const mcpList = useStore((s) => s.mcpList)
  const chatSkillIds = useStore((s) => s.chatSkillIds)
  const chatMcpIds = useStore((s) => s.chatMcpIds)
  const toggleChatSkill = useStore((s) => s.toggleChatSkill)
  const toggleChatMcp = useStore((s) => s.toggleChatMcp)
  const focusSettings = useStore((s) => s.focusSettings)

  const isSkills = kind === 'skills'
  const items = isSkills ? skillsList.map((s) => ({ id: s.id, label: s.name })) : mcpList.map((m) => ({ id: m.id, label: m.label }))
  const active = isSkills ? chatSkillIds : chatMcpIds
  const toggle = isSkills ? toggleChatSkill : toggleChatMcp

  return (
    <>
      <button onClick={onBack} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[12px] font-medium text-fg-muted hover:bg-[var(--color-surface-3)]">
        <ChevronLeft size={14} /> {isSkills ? 'Skills' : 'Connectors'} · this chat
      </button>
      <div className="my-1 h-px bg-[var(--line-soft)]" />
      <div className="max-h-56 overflow-y-auto">
        {items.length === 0 && <div className="px-2.5 py-2 text-[12px] text-fg-faint">None yet — add one in settings.</div>}
        {items.map((it) => (
          <button key={it.id} onClick={() => toggle(it.id)} className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-fg-muted transition-colors hover:bg-[var(--color-surface-3)] hover:text-fg">
            <span className="flex-1 truncate">{it.label}</span>
            {active.includes(it.id) && <Check size={14} className="text-[var(--color-accent-bright)]" />}
          </button>
        ))}
      </div>
      <div className="my-1 h-px bg-[var(--line-soft)]" />
      <button onClick={() => { onClose(); focusSettings(kind) }} className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[12px] text-fg-subtle hover:bg-[var(--color-surface-3)] hover:text-fg">
        {isSkills ? <BookOpen size={14} /> : <Boxes size={14} />} Manage in settings ›
      </button>
    </>
  )
}
